export interface TransitionOptions {
  element: HTMLElement;
  before?: () => void;
  transition: () => void;
  after?: () => void;
  event?: string;
  fallback?: boolean;
  property?: string;
}

type TransitionOptionsWithDefaults = Omit<TransitionOptions, 'event'> & {
  event: string;
};

const FALLBACK_TIMEOUT = 150;

function hasRunningTransition(element: HTMLElement, property?: string): boolean {
  if (typeof element.getAnimations !== 'function') {
    return true;
  }

  return element.getAnimations().some((animation) => {
    if (!('transitionProperty' in animation)) {
      return false;
    }

    return property === undefined || (animation as CSSTransition).transitionProperty === property;
  });
}

export function watchForMissingTransition(element: HTMLElement, onMissing: () => void, property?: string): () => void {
  let deadline = performance.now() + FALLBACK_TIMEOUT;
  let frame: number | undefined;

  const check = (now: number): void => {
    if (hasRunningTransition(element, property)) {
      deadline = now + FALLBACK_TIMEOUT;
    } else if (now >= deadline) {
      frame = undefined;
      onMissing();

      return;
    }

    frame = requestAnimationFrame(check);
  };

  if (typeof element.getAnimations === 'function') {
    frame = requestAnimationFrame(check);
  }

  return () => {
    if (frame !== undefined) {
      cancelAnimationFrame(frame);
      frame = undefined;
    }
  };
}

/**
 * A helper for handling transitions.
 */
export class Transition {
  private options: TransitionOptionsWithDefaults;
  private onEnd: (e: Event) => void;
  private cancelFallback?: () => void;
  private finished = false;

  constructor(options: TransitionOptions) {
    this.options = {
      event: 'transitionend',
      ...options,
    };

    this.onEnd = this.handleEnd.bind(this);

    this.options.element.addEventListener(this.options.event, this.onEnd);

    // Fire a callback function before the transition starts
    this.options.before?.();

    // Perform the transition
    this.options.transition();

    if (this.options.fallback) {
      this.cancelFallback = watchForMissingTransition(this.options.element, () => this.finish(), this.options.property);
    }
  }

  private handleEnd(e: Event): void {
    // Ignore events that bubble up from child elements
    if (e.target !== this.options.element) {
      return;
    }

    // Ignore the other properties transitioning on the same element
    if (this.options.property && (e as TransitionEvent).propertyName !== this.options.property) {
      return;
    }

    this.finish();
  }

  /**
   * Run the `after` callback once, whether the transition actually happened or not.
   */
  private finish(): void {
    if (this.finished) {
      return;
    }

    this.finished = true;

    // Remove the event listeners to avoid duplicate behaviors
    this.destroy();

    // Fire a callback function after the transition ends
    this.options.after?.();
  }

  /**
   * Public
   */
  public destroy(): void {
    this.cancelFallback?.();
    this.cancelFallback = undefined;

    this.options.element.removeEventListener(this.options.event, this.onEnd);
  }
}
